import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Download,
  Upload,
  X,
  CheckCircle,
  Warning,
  XCircle,
  FileArrowUp,
  Trash,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { formatINR } from "../../lib/loanMath";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_CHIP = {
  valid: { icon: CheckCircle, color: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30", label: "Valid" },
  warning: { icon: Warning, color: "text-amber-600 bg-amber-500/10 border-amber-500/30", label: "Warning" },
  error: { icon: XCircle, color: "text-red-600 bg-red-500/10 border-red-500/30", label: "Error" },
};

const EDITABLE_FIELDS = [
  { key: "name", label: "Nickname", type: "text" },
  { key: "lender_name", label: "Lender", type: "text" },
  { key: "interest_rate_pct", label: "Rate %", type: "number", step: 0.05 },
  { key: "current_balance", label: "Outstanding ₹", type: "number" },
  { key: "current_emi", label: "EMI ₹", type: "number" },
  { key: "remaining_tenure_months", label: "Tenure (mo)", type: "number" },
];

/**
 * Bulk Upload Dialog — Feature 1 of the Loan Rebalancer suite.
 * Flow: download template → upload .xlsx/.csv → preview with chips → inline
 * edit any cell / drop a row / re-validate → pick ADD vs REPLACE → import.
 */
export default function BulkUploadDialog({ open, onClose, planName, onImported }) {
  const inputRef = useRef(null);
  const [stage, setStage] = useState("pick"); // pick | preview | confirming | importing | done
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState("add");    // add | replace
  const [dragOver, setDragOver] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadTemplate = useCallback(() => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/api/loans/template.xlsx`;
    a.download = "Estima_Loan_Upload_Template.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Template downloaded");
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".csv")) {
      toast.error("Please upload a .xlsx or .csv file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File is too large. Please keep it under 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (planName) fd.append("plan_name", planName);
      const { data } = await api.post("/loans/parse-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("preview");
    } catch (e) {
      toast.error("Could not read the file: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  }, [planName]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  }, [handleFile]);

  const editCell = (rowIdx, key, value) => {
    setPreview((p) => {
      const rows = [...p.rows];
      const row = { ...rows[rowIdx] };
      row.loan = { ...row.loan, [key]: value };
      // Re-run a light client-side EMI<interest check so the user gets
      // immediate feedback. Full server re-validation happens on next upload.
      const bal = Number(row.loan.current_balance) || 0;
      const emi = Number(row.loan.current_emi) || 0;
      const rate = Number(row.loan.interest_rate_pct) || 0;
      const monthlyInt = (bal * rate) / 1200;
      const prevErrors = (row.errors || []).filter(
        (e) => !e.toLowerCase().includes("emi does not cover")
      );
      if (bal > 0 && emi > 0 && emi <= monthlyInt) {
        prevErrors.push(
          "Your EMI does not cover the monthly interest. Please check EMI or rate."
        );
      }
      row.errors = prevErrors;
      row.status = row.errors.length ? "error" : (row.warnings?.length ? "warning" : "valid");
      rows[rowIdx] = row;
      const summary = {
        total: rows.length,
        valid: rows.filter((r) => r.status === "valid").length,
        warning: rows.filter((r) => r.status === "warning").length,
        error: rows.filter((r) => r.status === "error").length,
      };
      return { ...p, rows, summary };
    });
  };

  const removeRow = (rowIdx) => {
    setPreview((p) => {
      const rows = p.rows.filter((_, i) => i !== rowIdx);
      const summary = {
        total: rows.length,
        valid: rows.filter((r) => r.status === "valid").length,
        warning: rows.filter((r) => r.status === "warning").length,
        error: rows.filter((r) => r.status === "error").length,
      };
      return { ...p, rows, summary };
    });
  };

  const doImport = async () => {
    if (!preview?.rows?.length) return;
    const importable = preview.rows.filter((r) => r.status !== "error");
    if (importable.length === 0) {
      toast.error("No valid rows to import. Please fix the errors.");
      return;
    }
    if (mode === "replace") {
      const ok = window.confirm(
        `Replace will DELETE all existing loans in "${planName || "My loan plan"}" and import these ${importable.length} loans. Continue?`
      );
      if (!ok) return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/loans/bulk-import", {
        plan_name: planName || "My loan plan",
        mode,
        rows: preview.rows,
      });
      setImportResult(data);
      setStage("done");
      toast.success(`${data.imported} loans imported, ${data.skipped} skipped`);
      onImported?.(data);
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = async () => {
    try {
      const { data } = await api.post(
        "/loans/upload-error-report",
        { plan_name: planName || "My loan plan", mode, rows: preview.rows },
        { responseType: "blob" }
      );
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Estima_Loan_Upload_Errors.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the error report.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-6"
      data-testid="bulk-upload-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border hairline rounded-lg w-full max-w-5xl my-6 shadow-2xl">
        <header className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <h2 className="font-serif text-xl">Bulk upload loans</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Download the template → fill → upload → preview → import.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
            data-testid="bulk-upload-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="p-6 space-y-5">
          {/* Template download */}
          <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b hairline">
            <div className="text-sm">
              <div className="font-medium">Step 1 — download template</div>
              <div className="text-xs text-muted-foreground">
                18 columns · dropdowns included · ≤ 25 loans per upload.
              </div>
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm"
              data-testid="bulk-upload-download-template"
            >
              <Download size={14} weight="bold" />
              Download Excel template
            </button>
          </div>

          {stage === "pick" && (
            <div
              className={`rounded-lg border-2 border-dashed hairline p-10 text-center transition-colors ${
                dragOver ? "bg-[hsl(var(--muted))]/60" : "bg-[hsl(var(--muted))]/20"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="bulk-upload-dropzone"
            >
              <FileArrowUp size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
              <div className="font-serif text-lg mb-1">
                Step 2 — drop your .xlsx or .csv here
              </div>
              <div className="text-xs text-muted-foreground mb-4">
                Max 2 MB · max 25 loans · we never store the raw file.
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="bulk-upload-file-input"
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm border hairline rounded-md"
                disabled={uploading}
                data-testid="bulk-upload-browse"
              >
                <Upload size={14} weight="bold" />
                {uploading ? "Reading…" : "Browse files"}
              </button>
            </div>
          )}

          {stage === "preview" && preview && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3" data-testid="bulk-upload-summary">
                <div className="text-sm">
                  <div className="font-medium">Step 3 — review and import</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <span className="text-emerald-600">{preview.summary.valid} valid</span> ·{" "}
                    <span className="text-amber-600">{preview.summary.warning} with warnings</span> ·{" "}
                    <span className="text-red-600">{preview.summary.error} with errors</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border hairline rounded-md cursor-pointer">
                    <input
                      type="radio"
                      name="import-mode"
                      checked={mode === "add"}
                      onChange={() => setMode("add")}
                      data-testid="bulk-upload-mode-add"
                    />
                    Add to existing
                  </label>
                  <label className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border hairline rounded-md cursor-pointer">
                    <input
                      type="radio"
                      name="import-mode"
                      checked={mode === "replace"}
                      onChange={() => setMode("replace")}
                      data-testid="bulk-upload-mode-replace"
                    />
                    Replace existing
                  </label>
                </div>
              </div>

              <div className="overflow-x-auto border hairline rounded-md" data-testid="bulk-upload-preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--muted))]/40 text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-2 w-20">Status</th>
                      {EDITABLE_FIELDS.map((f) => (
                        <th key={f.key} className="text-left px-2 py-2">{f.label}</th>
                      ))}
                      <th className="text-left px-2 py-2">Reason</th>
                      <th className="text-right px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => {
                      const chip = STATUS_CHIP[r.status];
                      const Icon = chip.icon;
                      const reasons = [...(r.errors || []), ...(r.warnings || [])];
                      return (
                        <tr
                          key={`row-${i}-${r.loan?.name || "x"}`}
                          className="border-t hairline"
                          data-testid={`bulk-upload-row-${i}`}
                        >
                          <td className="px-2 py-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${chip.color}`}>
                              <Icon size={11} weight="bold" />
                              {chip.label}
                            </span>
                          </td>
                          {EDITABLE_FIELDS.map((f) => (
                            <td key={f.key} className="px-1 py-1">
                              <input
                                type={f.type}
                                step={f.step}
                                value={r.loan?.[f.key] ?? ""}
                                onChange={(e) => editCell(
                                  i, f.key,
                                  f.type === "number" ? Number(e.target.value) : e.target.value
                                )}
                                className="w-full bg-background border hairline rounded px-2 py-1 text-xs"
                                data-testid={`bulk-upload-cell-${i}-${f.key}`}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-2 text-[11px] text-muted-foreground max-w-[260px]">
                            <div className="space-y-1">
                              {reasons.slice(0, 2).map((rs) => (
                                <div key={rs}>{rs}</div>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button
                              onClick={() => removeRow(i)}
                              className="text-muted-foreground hover:text-destructive"
                              aria-label="Remove row"
                              data-testid={`bulk-upload-remove-${i}`}
                            >
                              <Trash size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {preview.rows.length === 0 && (
                      <tr><td colSpan={EDITABLE_FIELDS.length + 3} className="px-4 py-6 text-center text-muted-foreground">
                        All rows removed. Close and start over.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => { setPreview(null); setStage("pick"); }}
                  className="btn-ghost px-4 py-2 text-sm border hairline rounded-md"
                  data-testid="bulk-upload-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={doImport}
                  disabled={importing || preview.rows.filter((r) => r.status !== "error").length === 0}
                  className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2"
                  data-testid="bulk-upload-import"
                >
                  {importing ? "Importing…" : `Import ${preview.rows.filter((r) => r.status !== "error").length} valid rows`}
                </button>
              </div>
            </>
          )}

          {stage === "done" && importResult && (
            <div className="text-center py-8 space-y-4" data-testid="bulk-upload-done">
              <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-500" />
              <div className="font-serif text-xl">
                {importResult.imported} loans imported, {importResult.skipped} skipped
              </div>
              <div className="text-xs text-muted-foreground">
                Plan "{importResult.plan_name}" now has {importResult.total_loans_in_plan} loans.
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                {importResult.skipped > 0 && (
                  <button
                    onClick={downloadErrorReport}
                    className="btn-ghost text-xs px-4 py-2 border hairline rounded-md"
                    data-testid="bulk-upload-error-report"
                  >
                    Download error report (.xlsx)
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="btn-primary text-sm px-5 py-2"
                  data-testid="bulk-upload-close-done"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <footer className="px-6 py-3 border-t hairline text-[11px] text-muted-foreground">
          Estimates for planning only. Not financial or tax advice. Please confirm exact figures with your lender.
        </footer>
      </div>
    </div>
  );
}

// Portfolio summary row — reused by LoanRebalancer to show weighted totals
export function PortfolioSummary({ loans }) {
  if (!loans?.length) return null;
  const totalOut = loans.reduce((s, l) => s + (Number(l.current_balance) || 0), 0);
  const totalEmi = loans.reduce((s, l) => s + (Number(l.current_emi) || 0), 0);
  const wSumRate = loans.reduce(
    (s, l) => s + (Number(l.current_balance) || 0) * (Number(l.interest_rate_pct) || 0),
    0
  );
  const wAvgRate = totalOut > 0 ? wSumRate / totalOut : 0;
  // Debt-free date = max closure across loans; here we approximate with max remaining months
  const maxRem = loans.reduce((m, l) => {
    const rem = Number(l.remaining_tenure_months) || (Number(l.tenure_years) || 0) * 12;
    return Math.max(m, rem);
  }, 0);
  const freeDate = new Date();
  freeDate.setMonth(freeDate.getMonth() + maxRem);
  const dfStr = freeDate.toLocaleDateString("en-IN", { month: "short", year: "numeric" });

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="loan-portfolio-summary">
      <SummaryCard label="Total outstanding" value={formatINR(totalOut, true)} tid="port-outstanding" />
      <SummaryCard label="Monthly EMI" value={formatINR(totalEmi, true)} tid="port-emi" />
      <SummaryCard label="Weighted avg rate" value={`${wAvgRate.toFixed(2)}%`} tid="port-rate" />
      <SummaryCard label="Debt-free by" value={dfStr} tid="port-free-date" />
    </div>
  );
}

function SummaryCard({ label, value, tid }) {
  return (
    <div className="card-flat p-3" data-testid={tid}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="font-serif text-lg mt-0.5">{value}</div>
    </div>
  );
}
